import React from "react";
import { Reveal } from "../Reveal";
import tankerDiagram from "../../assets/tanker-diagram.png";
import diamant from "../../assets/diamant.png";
import chimpChampChief from "../../assets/chimpchampchief.png";
import baliPhoto from "../../assets/bali.webp";

const introText =
  "Jeg er drevet af nysgerrighed. Jeg elsker at udforske, hvordan mennesker udvikler sig – gennem samtaler, bøger, forskning og mødet med forskellige mennesker, kulturer og fællesskaber. Her deler jeg løbende refleksioner, videoer og artikler med inspiration fra mennesker, jeg har talt med, bøger og podcasts, der har gjort indtryk på mig, og erfaringer fra mit arbejde. Måske finder du også noget, der sætter nye perspektiver i gang hos dig.";

// Image + text articles (alternating image side)
const articles = [
  {
    title: "Hvor kommer vores tanker og følelser fra?",
    image: tankerDiagram,
    imageRight: true,
    contain: true,
    link: null,
    blocks: [
      { type: "p", text: "Hvor kommer vores tanker og følelser fra? Det findes der mange forskellige bud på. En fælles pointe på tværs af flere moderne psykologiske retninger er dog, at det ikke er vores tanker og følelser i sig selv, der skaber problemer – men vores forhold til dem. Vi kan sjældent kontrollere, hvilke tanker eller følelser der opstår, hvornår de kommer, eller hvor stærke de er. Til gengæld kan vi øve os i at blive bevidste om dem, så de ikke automatisk får lov til at styre vores handlinger." },
      { type: "p", text: "Når vi ukritisk tror på vores tanker eller lader vores følelser diktere vores adfærd, begynder de at forme vores handlinger. Gentagne handlinger bliver til vores vaner, og over tid er vanerne med til at forme vores karakter. Det menneske vi er." },
      { type: "p", text: "Derfor begynder forandring ikke nødvendigvis med at tænke mere positivt eller føle noget andet. Den begynder med bevidsthed." },
    ],
  },
  {
    title: "Mød doubleyou's chimp, champ & chief",
    image: chimpChampChief,
    imageRight: false,
    contain: true,
    link: "https://www.linkedin.com/feed/update/urn:li:activity:7426176717825765376",
    doubleyou: true,
    blocks: [
      { type: "p", text: "Chimp og champ er hjernens to mentale systemer – og mellem dem opstår ofte en indre kamp, når der er noget på spil. Chief er den, der skelner og afgør retningen." },
      { type: "p", text: "Når du kender dine værdier og har en retning, bliver det nemmere at navigere mellem chimp og champ og træffe beslutninger, der stemmer overens med det menneske, du gerne vil være 🎯" },
    ],
  },
  {
    title: "Personlig udvikling for højskoleelever",
    image: baliPhoto,
    imageRight: true,
    contain: false,
    big: true,
    link: "https://www.linkedin.com/feed/update/urn:li:activity:7449737823752077312/",
    blocks: [
      { type: "p", text: "Begreberne fra doubleyou – chimp og champ – nåede hele vejen til Bali, da jeg underviste højskoleelever i værdier, mod og mental styrke." },
      { type: "p", text: "På højskolen arbejder eleverne med deres indre kompas – at skabe bevidsthed om, hvad de vil, og hvilke værdier der skal drive dem undervejs. I arbejdet med dette gør vi os nysgerrige på os selv og dykker ned i spørgsmål som:" },
      { type: "ul", items: [
        "Hvad handler ubehaget egentlig om, når vi reagerer i vores chimp – automatisk og reaktivt – og dermed går imod det, vi i virkeligheden gerne vil?",
        "Hvilke svære tanker og følelser dukker op i os?",
        "Hvad er vi bange for at mærke?",
      ] },
      { type: "p", text: "Og når vi er klogere på det: Hvordan kan vi blive bedre til at håndtere det, så det ikke styrer os?" },
    ],
  },
];

const billingVideos = [
  { id: "udenfor-banen", src: "/media/billing-udenfor-banen.mp4", poster: "/media/billing-udenfor-banen-poster.jpg" },
  { id: "ny-rolle", src: "/media/billing-ny-rolle.mp4", poster: "/media/billing-ny-rolle-poster.jpg" },
  { id: "praestationsangst", src: "/media/praestationsangst.mp4", poster: "/media/praestationsangst-poster.jpg" },
];

// Text-only reflection (keeps the "Refleksion" eyebrow)
const reflection = {
  title: "Når der er noget på spil",
  link: "https://www.linkedin.com/feed/update/urn:li:activity:7459210042391724033/",
  paragraphs: [
    "Hvorfor elsker vi det? At løbe eller se et maraton. At følge med i sport. Suset, adrenalinen, håbet og tankerne om at lykkes.",
    "Fordi ingenting er sikkert.",
    "Vi rummer usikkerheden og nervøsiteten, fordi vi ved, det er en del af gamet.",
    "Det er derfor, det er fedt.",
    "Vi ved, at vejen til målet ikke er lineær, og at modgang er en del af rejsen. Men vi håndterer det og kommer videre, fordi håbet til at lykkes er større end frygten for at mislykkes.",
  ],
};

const yogaPost = {
  title: "Når det ydre er et udtryk for det indre",
  intro: [
    "Som yoga-underviser er det ikke nok at kende kroppens fysiologi eller yogaens principper. Du skal kunne skabe et rum, hvor mennesker både bliver motiverede til at yde deres bedste og samtidig finder nærvær, ro og adgang til et meditativt state of mind.",
    "På en Bikram Yoga Teacher Training-uddannelse, hvor jeg underviste, legede vi derfor med kropssprog, stemme og fremtoning. Små ændringer i måden, vi står, bevæger os eller taler på, er med til at skabe en energi og stemning samt vidt forskellige oplevelser for dem, der bliver undervist.",
    "I forlængelse af det kiggede vi på deres grounding.",
  ],
  grounding: [
    "Hvad karakteriserer os, når vi er groundede? Rolige, nærværende, selvsikre og i kontakt med os selv. Og hvad sker der, når vi falder ud af vores grounding? Måske begynder vi at tale hurtigere eller langsommere, bevæge os mere eller mindre, blive mindre autentiske og flytte vores fokus væk fra nuet.",
    "Her brugte vi bl.a. den kognitive diamant til at opdage, hvilke tanker og følelser der opstår, og hvad det gør ved vores krop og vores adfærd. Det ser forskelligt ud for os hver især, og derfor er det vigtigt at blive bevidst. Bevidst om, hvem jeg ønsker at være, og hvordan det skal komme til udtryk.",
    "Jo bedre vi forstår, hvad der sker i vores indre, jo hurtigere kan vi vende tilbage til nuet. Til groundingen, hvor nærvær og autenticitet er stærkest.",
  ],
  question: "Hvad er dine tegn på, at du er groundet? Og hvordan mærker du, når du mister den?",
};

const LinkedInMark = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4">
    <path d="M20.45 20.45h-3.56v-5.57c0-1.33-.02-3.04-1.85-3.04-1.85 0-2.14 1.45-2.14 2.94v5.67H9.35V9h3.42v1.56h.05c.48-.9 1.64-1.85 3.37-1.85 3.6 0 4.27 2.37 4.27 5.46v6.28zM5.34 7.43a2.07 2.07 0 1 1 0-4.14 2.07 2.07 0 0 1 0 4.14zM7.12 20.45H3.55V9h3.57v11.45zM22.22 0H1.77C.79 0 0 .77 0 1.72v20.56C0 23.23.79 24 1.77 24h20.45c.98 0 1.78-.77 1.78-1.72V1.72C24 .77 23.2 0 22.22 0z" />
  </svg>
);

const LinkedInLink = ({ href }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="group/link mt-6 inline-flex items-center gap-2 text-[15px] font-semibold text-[#852936] transition-colors hover:text-[#6E1F2B]"
    data-testid="preview-linkedin-post-link"
  >
    <LinkedInMark />
    Se opslaget på LinkedIn
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      className="h-4 w-4 transition-transform duration-300 group-hover/link:translate-x-1">
      <path d="M5 12h14M13 6l6 6-6 6" />
    </svg>
  </a>
);

const Blocks = ({ blocks }) => (
  <div className="mt-4 space-y-3 text-[16px] leading-relaxed text-[#111827]/70">
    {blocks.map((b, i) =>
      b.type === "ul" ? (
        <ul key={i} className="space-y-2 pl-1">
          {b.items.map((it, j) => (
            <li key={j} className="flex gap-3">
              <span className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[#852936]" />
              <span>{it}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p key={i}>{b.text}</p>
      )
    )}
  </div>
);

const MediaArticle = ({ article }) => (
  <Reveal delay={80}>
    <article
      className="grid items-center gap-8 rounded-3xl border border-black/[0.06] bg-white p-6 sm:p-8 lg:grid-cols-2 lg:gap-12 preview-shadow-soft"
      data-testid="preview-inspiration-article"
    >
      <div className={article.imageRight ? "order-2 lg:order-1" : "order-2 lg:order-2"}>
        <h3 className="font-display text-xl font-semibold leading-snug text-[#111827] sm:text-2xl">
          {article.title}
        </h3>
        <Blocks blocks={article.blocks} />
        {article.link && <LinkedInLink href={article.link} />}
        {article.doubleyou && (
          <a
            href="https://www.doubleyou-partners.com"
            target="_blank"
            rel="noopener noreferrer"
            className="group/link mt-3 flex w-fit items-center gap-2 text-[15px] font-semibold text-[#852936] transition-colors hover:text-[#6E1F2B]"
            data-testid="preview-doubleyou-more-link"
          >
            Læs mere om doubleyou her
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
              className="h-4 w-4 transition-transform duration-300 group-hover/link:translate-x-1">
              <path d="M5 12h14M13 6l6 6-6 6" />
            </svg>
          </a>
        )}
      </div>
      <div className={article.imageRight ? "order-1 lg:order-2" : "order-1 lg:order-1"}>
        <div className={`group flex justify-center overflow-hidden rounded-2xl ${article.contain ? "bg-[#FBFBF9]" : ""}`}>
          <img
            src={article.image}
            alt={article.title}
            className={
              article.contain
                ? "max-h-[300px] w-auto object-contain"
                : `${article.big ? "aspect-square" : "aspect-[4/3]"} w-full object-cover`
            }
          />
        </div>
      </div>
    </article>
  </Reveal>
);

const PreviewInspiration = () => (
  <section id="perspektiver" className="relative overflow-hidden bg-[#F4F1EC] py-16 sm:py-24" data-testid="preview-section-perspektiver">
    <div className="pointer-events-none absolute -left-32 top-0 h-[440px] w-[440px] rounded-full bg-[#852936]/[0.06] blur-[130px]" />
    <div className="relative mx-auto max-w-6xl px-5 sm:px-8">
      <Reveal>
        <div className="mb-7 h-0.5 w-12 bg-[#852936]" />
        <h2
          className="font-display text-4xl font-semibold leading-[1.02] tracking-tight text-[#852936] sm:text-5xl md:text-6xl lg:text-7xl"
          data-testid="preview-heading-perspektiver"
        >
          Perspektiver
        </h2>
        <p className="mt-8 max-w-3xl text-[17px] leading-relaxed text-[#111827]/70" data-testid="preview-inspiration-intro">
          {introText}
        </p>
      </Reveal>

      {/* Philip Billing */}
      <Reveal delay={80}>
        <div className="mt-14 rounded-3xl border border-black/[0.06] bg-white p-6 sm:p-8 preview-shadow-soft" data-testid="preview-inspiration-billing">
          <span className="eyebrow mb-3 block text-[11px] font-semibold text-[#852936]">Video</span>
          <h3 className="font-display text-xl font-semibold leading-snug text-[#111827] sm:text-2xl">
            Om mod med Philip Billing
          </h3>
          <p className="mt-4 max-w-3xl text-[16px] leading-relaxed text-[#111827]/70" data-testid="preview-inspiration-billing-text">
            Jeg tog en snak med fodboldspiller på herrelandsholdet og i FCM, Philip Billing, om mod
            udenfor banen, forventninger til ham samt præstationsangst og håndtering af svære tanker og
            følelser på fodboldbanen. Få et smugkig her!
          </p>
          <div className="mt-8 grid gap-5 sm:grid-cols-3">
            {billingVideos.map((v) => (
              <div key={v.src} className="overflow-hidden rounded-2xl bg-black preview-shadow-lift">
                <video
                  controls
                  playsInline
                  preload="metadata"
                  poster={v.poster}
                  className="aspect-[9/16] h-auto w-full object-cover"
                  data-testid={`preview-inspiration-video-${v.id}`}
                >
                  <source src={v.src} type="video/mp4" />
                  Din browser understøtter ikke video.
                </video>
              </div>
            ))}
          </div>
        </div>
      </Reveal>

      {/* Text-only reflection */}
      <Reveal delay={80}>
        <div className="mt-14 rounded-3xl border border-black/[0.06] bg-white p-6 sm:p-8 preview-shadow-soft" data-testid="preview-inspiration-reflection">
          <span className="eyebrow mb-3 block text-[11px] font-semibold text-[#852936]">Refleksion</span>
          <h3 className="font-display text-xl font-semibold leading-snug text-[#111827] sm:text-2xl">
            {reflection.title}
          </h3>
          <div className="mt-4 space-y-3 text-[16px] leading-relaxed text-[#111827]/70">
            {reflection.paragraphs.map((p, j) => (
              <p key={j}>{p}</p>
            ))}
          </div>
          <LinkedInLink href={reflection.link} />
        </div>
      </Reveal>

      {/* Yoga reflection with video */}
      <Reveal delay={80}>
        <div className="mt-6 rounded-3xl border border-black/[0.06] bg-white p-6 sm:p-8 preview-shadow-soft" data-testid="preview-inspiration-yoga">
          <span className="eyebrow mb-3 block text-[11px] font-semibold text-[#852936]">Refleksion</span>
          <h3 className="font-display text-xl font-semibold leading-snug text-[#111827] sm:text-2xl">
            {yogaPost.title}
          </h3>

          {/* Intro + video */}
          <div className="mt-6 grid gap-8 lg:grid-cols-2 lg:items-center lg:gap-10">
            <div className="space-y-3 text-[16px] leading-relaxed text-[#111827]/70">
              <p className="font-semibold text-[#111827]">Hvordan påvirker vi energien i rummet?</p>
              {yogaPost.intro.map((p, j) => (
                <p key={j}>{p}</p>
              ))}
            </div>
            <div className="overflow-hidden rounded-2xl bg-black preview-shadow-soft">
              <video
                controls
                playsInline
                muted
                preload="metadata"
                poster="/media/yoga-undervisning-poster.jpg"
                className="h-auto w-full"
                data-testid="preview-inspiration-yoga-video"
              >
                <source src="/media/yoga-undervisning.mp4" type="video/mp4" />
                Din browser understøtter ikke video.
              </video>
            </div>
          </div>

          {/* Diamant + grounding */}
          <div className="mt-8 grid gap-8 lg:grid-cols-2 lg:items-center lg:gap-10">
            <div className="order-2 flex justify-center overflow-hidden rounded-2xl bg-[#FBFBF9] preview-shadow-soft lg:order-1">
              <img
                src={diamant}
                alt="Den kognitive diamant"
                className="max-h-[320px] w-auto object-contain"
                data-testid="preview-inspiration-yoga-diamant"
              />
            </div>
            <div className="order-1 space-y-3 text-[16px] leading-relaxed text-[#111827]/70 lg:order-2">
              {yogaPost.grounding.map((p, j) => (
                <p key={j}>{p}</p>
              ))}
            </div>
          </div>

          <p className="mt-8 font-semibold text-[16px] text-[#111827]">{yogaPost.question}</p>
        </div>
      </Reveal>

      {/* Image + text articles */}
      <div className="mt-6 space-y-6">
        {articles.map((a, i) => (
          <MediaArticle key={i} article={a} />
        ))}
      </div>
    </div>
  </section>
);

export default PreviewInspiration;
